/*
 * The GEAR Store (thegearstorepk.com). Original work, not licensed for reuse.
 * See NOTICE.md. This header is a provenance marker — keep it.
 */

/*
 * The comment above does not survive: Shopify minifies this file on serve and
 * strips it, so it marks nothing on a site that copied the served asset. The
 * line below is the same marker written as code, which a minifier keeps —
 * assigning to a property is a side effect, so it cannot be dropped as dead
 * code, and property names on the global object are not renamed.
 *
 * Inert by design, and it must stay that way: one string pushed onto one
 * global. It changes no behaviour, measures nothing, and sends nothing
 * anywhere. Its only job is that `window.TGSOriginal` on somebody else's
 * storefront lists which of these files they took, and when they took them.
 */
window.TGSOriginal = window.TGSOriginal || [];
window.TGSOriginal.push('thegearstorepk.com/tgs-home-modules@2026-09-21');

/*
 * Click tracking for the home page's product rails, fandom tiles and trust row.
 *
 * WHY THIS EXISTS
 *
 * The page was rebuilt on 21 September 2026 around rails and tiles, and the
 * question is whether those blocks earn the space they take. An A/B test cannot
 * answer it: at 220 sessions a day, detecting a 20% change in checkout rate
 * needs about two years of traffic. Clicks happen often enough to read in days,
 * so this counts them instead.
 *
 * WHERE THE NUMBERS GO
 *
 * Google Analytics 4 (G-D9ERW9N6X4), which the store already loads, using two
 * of GA4's own recommended events rather than invented ones:
 *
 *   select_item       a product card in a rail    -> Reports, item list name
 *   select_promotion  a fandom tile, a trust row  -> Reports, promotion name
 *                     promise, a rail's View all
 *
 * Both are built-in GA4 dimensions, so the reports work with no custom
 * dimensions registered in the GA4 admin. That is the whole reason for using
 * them: a custom event name with custom parameters shows up as a row with no
 * detail until somebody sets up dimensions, and then quietly loses the data
 * recorded before that.
 *
 * WHAT IT DOES NOT DO
 *
 * It sends no identifiers of its own, sets no cookies and stores nothing. It
 * hands GA4 the same product, list and position a merchandising report would
 * show. It never delays or blocks a navigation. If GA4 is absent, or the
 * shopper has not allowed analytics, it does nothing at all.
 *
 * KEEPING IT WORKING
 *
 * The rail selectors below belong to Dawn, not to TGS, so a Dawn upgrade can
 * move them. The module then stops being counted, silently — nothing breaks on
 * the page. After an upgrade, check a rail click still shows up in GA4 realtime.
 */
(() => {
  if (window.TGSHomeModulesLoaded) return;
  window.TGSHomeModulesLoaded = true;

  const send = (name, payload) => {
    if (typeof window.gtag !== 'function') return;
    // Shopify's own consent gate. Absent on stores with no banner, which is
    // this one today, so an undefined answer counts as allowed.
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
      if (privacy.analyticsProcessingAllowed() === false) return;
    }
    window.gtag('event', name, payload);
  };

  const textOf = (node) => (node ? node.textContent.trim().replace(/\s+/g, ' ') : '');

  // The heading a merchant typed, which is what makes a row in the report
  // readable: "Gunpla, your next obsession" rather than a section id.
  const railName = (rail) => textOf(rail.querySelector('.collection__title h2, .collection__title h3')) || 'Untitled rail';

  const positionOf = (item, all) => Array.prototype.indexOf.call(all, item) + 1;

  document.addEventListener(
    'click',
    (event) => {
      // Links only. The quick-add button inside a card is a different act from
      // choosing to go and look at the product, and counting them together
      // would make a rail look busier than it is.
      const link = event.target.closest('a[href]');
      if (!link) return;

      const tile = link.closest('.fandom-tiles__tile');
      if (tile) {
        const all = tile.parentElement.querySelectorAll('.fandom-tiles__tile');
        send('select_promotion', {
          promotion_id: 'fandom-' + (link.getAttribute('href') || '').split('/').pop(),
          promotion_name: textOf(tile.querySelector('.fandom-tiles__label')),
          creative_name: 'Shop by fandom',
          creative_slot: String(positionOf(tile, all)),
        });
        return;
      }

      const promise = link.closest('.trust-row__item');
      if (promise) {
        const all = promise.parentElement.querySelectorAll('.trust-row__item');
        send('select_promotion', {
          promotion_id: 'trust-row',
          promotion_name: textOf(promise.querySelector('.trust-row__title')),
          creative_name: 'Trust row',
          creative_slot: String(positionOf(promise, all)),
        });
        return;
      }

      const rail = link.closest('.collection');
      if (!rail) return;

      const card = link.closest('.card-wrapper');
      if (!card) {
        // The rail's own "View all". Worth counting: a rail that sends people
        // into the collection is doing its job even when no card is clicked.
        if (link.closest('.collection__view-all')) {
          send('select_promotion', {
            promotion_id: 'rail-view-all',
            promotion_name: railName(rail),
            creative_name: 'Rail view all',
            creative_slot: '0',
          });
        }
        return;
      }

      const heading = card.querySelector('.card__heading a') || link;
      const handle = (heading.getAttribute('href') || '').split('/products/').pop().split('?')[0];
      const all = rail.querySelectorAll('.card-wrapper');

      // The numeric product id, so a row here lines up with the same product in
      // Merchant Center and Google Ads. The quick-add form carries it outright;
      // where quick-add is off, the heading link's id ends in it. Which prefix
      // that id uses depends on the card style the theme is set to
      // (CardLink-, StandardCardNoMediaLink-, and others), so the last run of
      // digits is read rather than a known prefix stripped. The handle is the
      // last resort: unique and readable, but it will not match an ad feed.
      const fromForm = card.querySelector('input[name="product-id"]');
      const fromLinkId = (heading.id.match(/(\d+)$/) || [])[1];
      const productId = (fromForm && fromForm.value) || fromLinkId || handle || 'unknown';

      send('select_item', {
        item_list_id: rail.dataset.id || 'rail',
        item_list_name: railName(rail),
        items: [
          {
            item_id: productId,
            item_name: textOf(heading) || handle,
            index: positionOf(card, all),
          },
        ],
      });
    },
    // Capture, so the count still happens if something further down stops the
    // event before it reaches the document.
    true
  );
})();
